import { EventEmitter, Injectable, Output } from '@angular/core';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Highlighter } from '../../models/table/highlight-resource';
import { JwtLegFiClaims } from '../auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { Payable } from '../../models/entities/payable';
import { map, tap } from 'rxjs/operators';
import { PayableApprover } from '../../models/entities/payableApprover';
import { PayableApproval } from '../../models/entities/payable-approvals';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { PDFSource } from 'ng2-pdf-viewer';
import { UploadedFile } from '../../models/entities/uploaded-file';
import { Payable as PayableInterface } from 'app/components/shared/http/request-interfaces';
import { Transaction } from '../../models/entities/transactions/transaction';
import moment from 'moment';
import { PayableFilterTab } from './payables-data.service';
import { PayableDatasourceOptions, PayablesResponse } from './payables.datasource';
import { ActivityLog } from '../../models/entities/activity-log';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root',
})
export class PayablesService
{
    @Output() updateWidgets = new EventEmitter<void>();

    private _http: ApplicationHttpClient;
    private _lobMailCheckFee: number = environment.LobMailCheckFeeCents;

    constructor(_http: ApplicationHttpClient) {
        this._http = _http;
    }

    /**
     * @param {number} organizationId
     * @param {PayableFilterTab} tab
     * @param {string} search
     * @param {string} sortColumn
     * @param {string} sortDirection
     * @param {number} page
     * @param {number} perPage
     * @param {PayableDatasourceOptions} options
     * @param {number} highlight
     * @returns {Observable<PayablesResponse>}
     */
    public getPayables(
            organizationId: number,
            tab: PayableFilterTab = 'unpaid',
            search = '',
            sortColumn: string = 'due_date',
            sortDirection: string = 'desc',
            page: number = 1,
            perPage: number = 25,
            options: PayableDatasourceOptions = {},
            highlight: Highlighter = null,
    ): Observable<PayablesResponse> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let params = new HttpParams()
            .append('organizationId', organizationId)
            .append('tab', tab)
            .append('search', search)
            .append('sortColumn', sortColumn)
            .append('sortDirection', sortDirection)
            .append('page', page.toString())
            .append('perPage', perPage.toString());

        if (Object.keys(options).length > 0) {
            params = params.append('filters', JSON.stringify(options));
        }

        if (highlight) {
            params = params.append('highlightId', highlight?.id);
            if (highlight?.refocus) {
                params = params.append('pageTo', 'highlighted');
            }
        }

        const url = Routes.MakeLegFiCoreUrl('/payables');
        return this._http.get(url, { params }).pipe(map((resp) => new PayablesResponse(resp)));

    }

    /**
     * @param {number} payableId
     */
    public getPayable(payableId: number): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId);
        return this._http.get(url).pipe(map((res: Object) => {
            return new Payable(res);
        }));
    }

    public previewPayable(
            request: PayableInterface.PreviewPayableRequest,
    ): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/preview');
        return this._http.post(url, JSON.stringify(request))
                .pipe(map((res: Object) => {
                    return new Payable(res);
                }));
    }

    public createPayable(request: {
        organizationId: number,
        payable: any,
        markAsPaid?: any,
        payableAction?: any
    }): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables');
        return this._http.post(url, JSON.stringify(request))
                .pipe(map((res: Object) => {
                    this.updateWidgets.emit();
                    return new Payable(res);
                }));
    }

    public updatePayablePaymentMethodPostApproval(
            payableId: number,
            payableData: PayableInterface.UpdatePayablePaymentMethodPostApprovalRequest,
    ): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/payment-method');
        return this._http.put(url, JSON.stringify(payableData))
                .pipe(map((res: Object) => {
                    this.updateWidgets.emit();
                    return new Payable(res);
                }));
    }

    public updatePayableDetails(
            payableId: number,
            payableData: PayableInterface.UpdatePayableDetailsRequest,
    ): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/details');
        return this._http.patch(url, JSON.stringify(payableData))
                .pipe(map((res: Object) => {
                    this.updateWidgets.emit();
                    return new Payable(res);
                }));
    }

    public attachInvoiceToPayable(
            uploadedFileId: number,
            inboundEmailId: number = null,
            payableId: number = null,
    ): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/invoice');
        return this._http.post(url, JSON.stringify({
            uploadedFileId: uploadedFileId,
            inboundEmailId: inboundEmailId,
            payableId: payableId,
            organizationId: jwt.orgId,
        }));
    }

    public cancelPayment(payableId: number, cancellationDate?: string): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/cancel');
        const payload = cancellationDate ? { cancellationDate } : {};
        return this._http.post(url, JSON.stringify(payload));
    }

    public deletePayable(payable: Payable): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payable.id);
        return this._http.delete(url).pipe(tap(() => this.updateWidgets.emit()));
    }

    public restorePayables(payableIds: number[]): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/restore');
        return this._http.post(url, JSON.stringify({payables: payableIds})).pipe(tap(() => this.updateWidgets.emit()));
    }

    public getApprovers(): Observable<PayableApprover[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers?organizationId=' + jwt.orgId);
        return this._http.get(url);
    }

    public updateApprovers(membershipIds: number[]): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers');
        return this._http.post(url, JSON.stringify({
            organizationId: jwt.orgId,
            membershipIds: membershipIds,
        }));
    }

    public getPayableRules(): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approval-rules?organizationId=' + jwt.orgId);
        return this._http.get(url);
    }

    public updatePayableApprovalRules(payableRules: object): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approval-rules');
        return this._http.post(url, JSON.stringify({
            orgId: jwt.orgId,
            payableRules: payableRules,
        }));
    }

    deletePayableRule(payableRuleId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approval-rules/' + payableRuleId);
        return this._http.delete(url);
    }

    setPayableApproval(request: PayableApproval): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let url = Routes.MakeLegFiCoreUrl('/payables/approvals');
        if (request.id && request.id > 0) {
            url += '/' + request.id;
            return this._http.put(url, JSON.stringify(request)).pipe(tap(() => this.updateWidgets.emit()));
        } else {
            return this._http.post(url, JSON.stringify(request)).pipe(tap(() => this.updateWidgets.emit()));
        }
    }

    getMappedPayableApproversAndApprovals(payableId: number): Observable<PayableApproval[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers-approvals/' + payableId);
        return this._http.get(url).pipe(map((response: Object[]) => {
            return response.map((obj) => {
                return new PayableApproval(obj);
            });
        }));
    }

    getWidgetsCount(): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/dashboard-widgets');
        return this._http.get(url);
    }

    sendPayableApprovalReminder(payableId: number, payableApproverId: number): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/approvers/' + payableApproverId + '/send-approval-reminder');
        return this._http.post(url, JSON.stringify({
            payableId: payableId,
            organizationId: jwt.orgId,
        }));
    }

    returnToDraft(payableId: number): Observable<Payable> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/' + payableId + '/returnToDraft');
        return this._http.post(url, JSON.stringify({}));
    }

    removeAttachment(payableId: number, attachmentId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/attachments/${attachmentId}`);
        return this._http.delete(url);
    }

    getInvoice(attachment: UploadedFile): Observable<PDFSource | string | ArrayBuffer> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(`/payables/${attachment.id}/download`);

        if (attachment?.fileType !== 'pdf' && attachment?.fileType !== 'docx') {
            return this._http.get(url);
        }

        const mimeType = attachment.fileType === 'pdf' ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

        const newHeaders = new HttpHeaders({ 'Content-Type': mimeType });

        return this._http.get(url, {
            headers: newHeaders,
            responseType: 'arraybuffer',
        });
    }

    public downloadPayableInvoice(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const downloadUrl = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/invoice-download`);

        return this._http.get(downloadUrl, {
            responseType: 'blob'
        }).pipe(
                map((response: Blob) => {
                    const contentType = response.type || 'application/octet-stream';

                    let extension = 'bin';
                    if (contentType.includes('pdf')) {
                        extension = 'pdf';
                    } else if (contentType.includes('jpeg') || contentType.includes('jpg')) {
                        extension = 'jpg';
                    } else if (contentType.includes('png')) {
                        extension = 'png';
                    } else if (contentType.includes('gif')) {
                        extension = 'gif';
                    } else if (contentType.includes('text')) {
                        extension = 'txt';
                    }

                    const filename = `invoice-${payableId}.${extension}`;

                    const blobUrl = window.URL.createObjectURL(response);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = filename;
                    link.style.visibility = 'hidden';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Clean up
                    window.URL.revokeObjectURL(blobUrl);
                    return true;
                })
        );
    }

    getPotentialTransactionMatches(payable: Payable): Observable<Transaction[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/potential-transaction-matches`);
        return this._http.post(url, JSON.stringify({
            organizationId: LegFiJwtService.read().orgId,
            payable: payable,
        }));
    }

    getPayableTrackingData(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/tracking`);
        return this._http.get(url);
    }

    minScheduledPaymentDate(): Date {
        const userTZ = moment.tz.guess();
        const userDateTime = moment().tz(userTZ);
        const estDateTime = userDateTime.clone().tz('America/New_York');
        const eleven55EST = moment.tz('America/New_York').set({hour: 11, minute: 55, second: 0, millisecond: 0});

        if (estDateTime.isBefore(eleven55EST)) {
            return new Date();
        } else {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow;
        }
    }

    downloadPrintableCheckFile(payable: Payable): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }
        const newHeaders = new HttpHeaders({
            'Content-Type': 'application/pdf',
        });

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payable.id}/download-printable-check`);

        return this._http.get(url, {
            headers: newHeaders,
            responseType: 'arraybuffer',
        });
    }

    downloadCheckImage(payableId: number): Observable<Blob> {
        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/check-image`);
        return this._http.get(url, { responseType: 'blob' });
    }

    getFormattedLobMailCheckFee(): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(this._lobMailCheckFee / 100);
    }

    getCheckPrintingSettings(): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/settings/check-printing?organizationId=' + jwt.orgId);
        return this._http.get(url);
    }

    updateCheckPrintingSettings(settings: any): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/settings/check-printing');
        return this._http.put(url, JSON.stringify({
            ...settings,
            organizationId: jwt.orgId,
        }));
    }

    getPayableFilters(): Observable<Object> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(`/payables/payableFilters`);

        return this._http.get(url);
    }

    getActivityLogs(
            filters?: {
                payableId?: number;
                eventTypes?: string[];
                dateFrom?: string;
                dateTo?: string;
            },
            page: number = 1,
            perPage: number = 25
    ): Observable<any> {
        const jwt = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let params = new HttpParams()
                .append('organizationId', jwt.orgId.toString())
                .append('page', page.toString())
                .append('perPage', perPage.toString());

        if (filters?.payableId) {
            params = params.append('payableId', filters.payableId.toString());
        }
        if (filters?.eventTypes?.length) {
            filters.eventTypes.forEach(type => {
                params = params.append('eventTypes[]', type);
            });
        }
        if (filters?.dateFrom) {
            params = params.append('dateFrom', filters.dateFrom);
        }
        if (filters?.dateTo) {
            params = params.append('dateTo', filters.dateTo);
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/settings/activity-logs');
        return this._http.get(url, { params });
    }

    /**
     * Clear all fulfillment errors for a payable
     */
    public clearFulfillmentErrors(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/clear-fulfillment-errors`);
        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Super admin action to manually mark a mailed check as processed.
     * Used when USPS fails to scan delivery events.
     */
    public markAsProcessed(payableId: number): Observable<any> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/mark-as-processed`);
        return this._http.post(url, JSON.stringify({}));
    }

    /**
     * Export payable attachments as ZIP file
     */
    exportPayableAttachments(payableId: number): Observable<Blob> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl(`/payables/${payableId}/export-attachments`);
        return this._http.post(url, null, { responseType: 'blob' });
    }

    /**
     * Export payables to CSV format
     * @param organizationId Organization ID
     * @param tab Current tab filter
     * @param search Search string
     * @param options Filter options
     * @returns Observable of Blob
     */
    public exportPayablesCsv(
        organizationId: number,
        tab: PayableFilterTab = 'unpaid',
        search: string = '',
        options: PayableDatasourceOptions = {}
    ): Observable<Blob> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        let params = new HttpParams()
            .append('organizationId', organizationId)
            .append('tab', tab)
            .append('search', search);

        // Add filter options to params
        if (Object.keys(options).length > 0) {
            params = params.append('filters', JSON.stringify(options));
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/export');
        return this._http.get(url, {
            params,
            responseType: 'blob'
        });
    }

    /**
     * Bulk export payables (invoices, attachments, or CSV)
     */
    public bulkExport(payableIds: number[], type: 'invoices' | 'attachments' | 'csv'): Observable<Blob> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url = Routes.MakeLegFiCoreUrl('/payables/bulk/export');
        return this._http.post(url, JSON.stringify({ payableIds, type }), {
            responseType: 'blob'
        });
    }
}
